// Phase G3 Stage 2: /play/who — multi-kid profile picker.
//
// This is the mid-session hand-off door PathHome's "kid-switch" chip
// (data-testid="kid-switch") links to — MA-288 slice 1 (classroom
// kiosk hand-off, epic; see docs/build/BACKLOG.md). Renders the SAME
// WhosPlayingScreen the cold-boot gate uses (App.jsx), so this route
// and the cold-boot picker can never visually/behaviorally drift apart.
//
// MA-288 slice 1 fix: the picker used to write the switch via a bare
// `setActiveChildIdState` — a raw useState setter that skipped the
// persisted-store write, the progress reload, and clearActiveSession()
// (a reload after switching reverted to the PREVIOUS kid; the new
// kid's name/mascot stayed stale on PathHome — a real, bite-proven bug,
// not a hypothetical). Now routes through `app.pickChild`, the same
// mount-frozen-safe function the cold-boot gate + (indirectly, via
// switchActiveChild) the parent-dashboard KidSwitcher already use.
//
// MA-288 classroom-roster-identity-write — the roster is no longer
// display-only. Tapping a real class_enrollments tile now:
//   1. optionally gates on the class's picture-secret (kid_secret_required,
//      migration 0042) via PictureSecretPad — a speed bump against a wrong
//      kid tapping a neighbor's tile, not authentication (no lockout; see
//      the gate handler's own comment);
//   2. calls ensureKioskChildForEntry (src/lib/teacher/kioskIdentity.ts) to
//      get/create the right LOCAL registry child for that enrollment — a
//      teacher-owned shadow student for an unclaimed row, or a
//      kiosk-claimed local record (no supabaseStudentId — see that
//      module's header) for a claimed one;
//   3. hands the resulting local id to `app.pickChild`, the SAME
//      mount-frozen-safe switch the family path below already uses — no
//      separate navigation call (pickChild itself navigates to /play in
//      both device modes, MA-853).
// A null result from step 2 (pre-founder-apply — migration 0042's RPCs
// aren't live yet on this Supabase project) leaves the tile tappable but
// shows an inline "not ready yet" note instead of throwing into the UI.
import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useAccount, useKids } from "@/lib/account";
import { useAppState } from "@/App/AppStateContext.tsx";
import WhosPlayingScreen from "@/screens/WhosPlayingScreen";
import { loadProgressForChild } from "@/childrenStore.js";
import KioskRosterScreen, { type DoorStatus } from "@/screens/KioskRosterScreen";
import PictureSecretPad from "@/components/kiosk/PictureSecretPad";
import { useArmedClass, pruneKioskChildrenAfterRosterRead } from "@/lib/teacher/kioskArming";
import { fetchClassRosterForKiosk, fetchKioskClassArchived, fetchKioskClassMeta, fetchKioskClassTeacherId, type KioskRosterEntry } from "@/lib/teacher/classApi";
import { DoorEndedScreen } from "@/screens/teacher/DoorEndedScreen";
import { ensureKioskChildForEntry } from "@/lib/teacher/kioskIdentity";
import { generateKidSecret, verifyKidSecretSelection, KID_SECRET_LENGTH } from "@/lib/teacher/kidSecret";

/** MA-925 — the door's roster read is bounded (RED round 1 N3). */
const DOOR_READ_TIMEOUT_MS = 15_000;

export const Route = createFileRoute("/play/who")({
  head: () => ({ meta: [{ title: "PencilWise · Who's playing?" }] }),
  component: ProfilePicker,
});

function ProfilePicker() {
  const app = useAppState();
  const { data: account } = useAccount();
  const { data: kids = [] } = useKids();
  // MA-288 kiosk-arming slice — a passive, read-only reflection of the
  // device's armed class (src/lib/teacher/kioskArming.ts).
  const armed = useArmedClass();

  // MA-288 classroom-roster-identity (read-only sub-slice) — once armed,
  // fetch the REAL class_enrollments roster (narrowed columns, no
  // claim_code — see fetchClassRosterForKiosk's own header) so the door
  // shows real names instead of the local family registry.
  //
  // MA-925 (D-TUX-6) — an ARMED door never falls back to the family picker
  // (on a teacher's own device that was the teacher's own children, shown to a
  // class). The read has three honest states: loading, error ("Can't load the
  // class list" + Try again) and ready (tiles, or "No names on this class list
  // yet", with Try again). No cached roster. The read is bounded: a read that
  // has not answered in DOOR_READ_TIMEOUT_MS shows the error state (with Try
  // again) instead of an endless placeholder.
  //
  // Pruning (D-1012-29, RED round 1 M1/S1): only after a successful,
  // NON-EMPTY read, only when the signed-in user is the class's own teacher
  // (an RLS-scoped read by a signed-out or foreign session answers `[]` or a
  // partial view), and only if the device is still armed for this class when
  // the prune runs — see pruneKioskChildrenAfterRosterRead. An empty read
  // deletes nothing.
  const [roster, setRoster] = useState<KioskRosterEntry[]>([]);
  const [doorStatus, setDoorStatus] = useState<DoorStatus>("loading");
  const [rosterAttempt, setRosterAttempt] = useState(0);
  const signedInUserId = app?.user?.id ?? null;
  useEffect(() => {
    if (!armed) {
      setRoster([]);
      setDoorStatus("loading");
      return;
    }
    let alive = true;
    const classId = armed.classId;
    setDoorStatus("loading");
    const timer = setTimeout(() => {
      if (!alive) return;
      alive = false;
      setRoster([]);
      setDoorStatus("error");
    }, DOOR_READ_TIMEOUT_MS);
    fetchClassRosterForKiosk(classId)
      .then(async (rows) => {
        if (!alive) return;
        clearTimeout(timer);
        setRoster(rows);
        setDoorStatus("ready");
        if (rows.length === 0) return;
        const classTeacherId = await fetchKioskClassTeacherId(classId);
        if (!alive) return;
        try {
          pruneKioskChildrenAfterRosterRead({
            classId,
            rosterEnrollmentIds: rows.map((r) => r.id),
            signedInUserId,
            classTeacherId,
          });
        } catch {
          /* private mode / quota — pruning is best-effort */
        }
      })
      .catch(() => {
        if (!alive) return;
        clearTimeout(timer);
        setRoster([]);
        setDoorStatus("error");
      });
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [armed?.classId, rosterAttempt, signedInUserId]);

  // MA-288 kid-secret sub-slice — the class's opt-in picture-secret gate.
  // Defaults to false (both before the fetch resolves and pre-founder-apply
  // — fetchKioskClassMeta's own 42703 guard already returns false for the
  // latter), matching "a kid reaches practice in ≤3 taps" out of the box.
  const [kidSecretRequired, setKidSecretRequired] = useState(false);
  useEffect(() => {
    if (!armed) {
      setKidSecretRequired(false);
      return;
    }
    let alive = true;
    fetchKioskClassMeta(armed.classId)
      .then((meta) => {
        if (alive) setKidSecretRequired(meta.kid_secret_required);
      })
      .catch(() => {
        if (alive) setKidSecretRequired(false);
      });
    return () => {
      alive = false;
    };
  }, [armed?.classId]);

  // MA-929 (D-TUX-5) — an ARCHIVED class's door shows "This class has ended"
  // and the grown-up exit, and no roster. "pending" until the class read
  // answers (bounded by the door's timeout): the door stays in its loading
  // state meanwhile, so no tile can be tapped into an ended class. null
  // (cannot tell: signed out, a foreign session, an error) keeps today's door;
  // the claim RPC refuses an archived class's codes server-side either way.
  const [archived, setArchived] = useState<boolean | null | "pending">("pending");
  useEffect(() => {
    if (!armed) {
      setArchived("pending");
      return;
    }
    let alive = true;
    setArchived("pending");
    const timer = setTimeout(() => {
      if (alive) setArchived(null);
    }, DOOR_READ_TIMEOUT_MS);
    fetchKioskClassArchived(armed.classId).then((value) => {
      if (!alive) return;
      clearTimeout(timer);
      setArchived(value);
    });
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [armed?.classId, rosterAttempt, signedInUserId]);

  // The enrollment currently behind the picture-secret overlay, if any.
  const [secretGateEntry, setSecretGateEntry] = useState<KioskRosterEntry | null>(null);
  const [secretGateShake, setSecretGateShake] = useState(false);
  // Per-tile busy/not-ready reflection for KioskRosterScreen (§ its own
  // props doc) — one at a time, since only one tile can be mid-ensure.
  const [busyEntryId, setBusyEntryId] = useState<string | null>(null);
  const [notReadyEntryId, setNotReadyEntryId] = useState<string | null>(null);

  const pick = (kidId: string) => {
    if (!account) return;
    // pickChild ALSO navigates — straight to /play in both device modes
    // (MA-853; it used to go through "/", whose front door sends a personal
    // device to /parent) — no separate navigate() call here, since a second
    // call would race it.
    app?.pickChild?.(kidId);
  };

  /** Ensures the local registry record for this enrollment, then picks it
   *  — the shared tail of both the direct-tap path (no secret required)
   *  and the post-verify path (secret required and correctly entered). */
  const ensureAndPick = async (entry: KioskRosterEntry) => {
    setBusyEntryId(entry.id);
    setNotReadyEntryId(null);
    try {
      const localId = await ensureKioskChildForEntry(entry, app?.user?.id ?? null);
      if (localId) {
        pick(localId);
      } else {
        // Pre-founder-apply: migration 0042's RPCs aren't live yet. The
        // door stays honest instead of throwing into the kid-facing UI.
        setNotReadyEntryId(entry.id);
      }
    } catch {
      setNotReadyEntryId(entry.id);
    } finally {
      setBusyEntryId(null);
    }
  };

  const onPickEntry = (entry: KioskRosterEntry) => {
    if (kidSecretRequired) {
      setSecretGateShake(false);
      setSecretGateEntry(entry);
      return;
    }
    void ensureAndPick(entry);
  };

  // MA-288 kid-secret sub-slice — verify the tapped sequence against the
  // SAME deterministic seed (the enrollment id) the teacher's own roster
  // chip derives from (see teacher.tsx's SecretRevealChip). No lockout: at
  // age 4-7 this is a speed bump against a wrong-kid tap, not
  // authentication, so a wrong sequence just clears and lets the kid try
  // again — never a cooldown, never a "too many attempts" dead end.
  const onSecretComplete = (selection: string[]) => {
    if (!secretGateEntry) return;
    const expected = generateKidSecret(secretGateEntry.id);
    if (verifyKidSecretSelection(selection, expected)) {
      const entry = secretGateEntry;
      setSecretGateEntry(null);
      setSecretGateShake(false);
      void ensureAndPick(entry);
    } else {
      setSecretGateShake(true);
    }
  };

  // MA-925 — armed means the class door, whatever the roster read did.
  const showRoster = Boolean(armed);

  if (armed && archived === true) {
    return <DoorEndedScreen className={armed.className} />;
  }

  if (secretGateEntry) {
    return (
      <div
        data-testid="kiosk-secret-gate"
        className="flex min-h-dvh flex-col items-center justify-center gap-6 bg-background px-6 py-10 font-sans text-foreground"
      >
        <p className="m-0 text-center font-serif text-2xl font-bold">
          Show us your secret, {secretGateEntry.display_name}!
        </p>
        {secretGateShake ? (
          <p data-testid="kiosk-secret-gate-wrong" className="m-0 text-sm font-bold text-destructive">
            Not quite — try again!
          </p>
        ) : null}
        {/* key forces a fresh PictureSecretPad (cleared tap progress) after
            a wrong sequence — the pad has no reset prop of its own, and
            re-mounting is simpler than adding one for this single caller. */}
        <PictureSecretPad key={secretGateShake ? "retry" : "initial"} length={KID_SECRET_LENGTH} onComplete={onSecretComplete} />
        <button
          type="button"
          data-testid="kiosk-secret-gate-back"
          onClick={() => {
            setSecretGateEntry(null);
            setSecretGateShake(false);
          }}
          className="min-h-[44px] min-w-[44px] px-4 text-sm font-semibold text-muted-foreground underline"
        >
          Back
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      {showRoster && armed ? (
        <KioskRosterScreen
          entries={roster}
          className={armed.className}
          onPickEntry={onPickEntry}
          busyEntryId={busyEntryId}
          notReadyEntryId={notReadyEntryId}
          status={archived === "pending" ? "loading" : doorStatus}
          onRetry={() => setRosterAttempt((n) => n + 1)}
        />
      ) : (
        <WhosPlayingScreen
          // MA-867 — each kid's own stored pal (progress.mascot.id), never
          // KidRow.mascot (registry-derived, always "blue").
          children={kids.map((k) => ({
            id: k.id,
            name: k.name,
            mascot: loadProgressForChild(k.id, () => null)?.mascot?.id ?? null,
          }))}
          onPick={pick}
          onAdd={() => {}}
          showAddTile={false}
        />
      )}
      {armed && (
        <p
          data-testid="kiosk-armed-caption"
          className="pointer-events-none absolute inset-x-0 bottom-3 text-center text-xs font-semibold text-muted-foreground"
        >
          Armed for {armed.className}
        </p>
      )}
    </div>
  );
}
